import { useEffect } from 'react';
import { useSiteName, useSiteTagline } from '../contexts/SiteConfigContext';

// index.html's <title>: the search-engine title for the site (its home page).
// Pages without a title of their own keep it rather than the tagline.
const INDEX_TITLE = typeof document === 'undefined' ? '' : document.title;

/**
 * Custom hook to dynamically set the document title based on site configuration
 * @param pageTitle Optional page-specific title (e.g., "Login", "Terms", etc.)
 */
export const useDocumentTitle = (pageTitle?: string) => {
  const siteName = useSiteName();
  const siteTagline = useSiteTagline();

  useEffect(() => {
    const siteTitle = INDEX_TITLE || `${siteName} - ${siteTagline}`;
    document.title = pageTitle ? `${pageTitle} - ${siteName}` : siteTitle;

    // A page title goes when its page does, so the next page never shows a
    // stale one (most pages set none and use the site title). The next page's
    // own title, if any, is set in the same commit, so nothing flickers.
    return () => {
      if (pageTitle) document.title = siteTitle;
    };
  }, [siteName, siteTagline, pageTitle]);
};

/**
 * Hook specifically for the default site title (home page)
 */
export const useDefaultDocumentTitle = () => {
  useDocumentTitle();
};

/**
 * Hook for page-specific titles
 * @param pageTitle The specific page title
 */
export const usePageDocumentTitle = (pageTitle: string) => {
  useDocumentTitle(pageTitle);
};
